import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Skeleton
} from '@mui/material';
import { Search as SearchIcon, SearchOff } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../../common/ProductCard';
import SEO from '../../common/SEO';
import { apiList, invokeGetApi } from '../../../services/apiServices';
import { products as localProducts } from '../../../config/products';

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';

  const [inputVal, setInputVal] = useState(query);
  const [allProducts, setAllProducts] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all products once
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const response = await invokeGetApi(apiList.getAllProducts);
        let fetched = [];
        if (response?.data?.products && Array.isArray(response.data.products)) {
          fetched = response.data.products;
        } else if (Array.isArray(response?.data)) {
          fetched = response.data;
        }
        setAllProducts(fetched.length > 0 ? fetched : localProducts);
      } catch {
        setAllProducts(localProducts);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Filter whenever query or products change
  useEffect(() => {
    if (!query.trim()) {
      setResults(allProducts);
      return;
    }
    const q = query.toLowerCase();
    const filtered = allProducts.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      String(p.category_id || '').includes(q)
    );
    setResults(filtered);
  }, [query, allProducts]);

  const handleSearch = (e) => {
    if (e.key === 'Enter' && inputVal.trim()) {
      setSearchParams({ q: inputVal.trim() });
    }
  };

  // Popular tags for empty/no-result state
  const popularTags = ['Windows 11', 'MS Office', 'Windows Server', 'Adobe', 'Tally', 'Visual Studio'];

  return (
    <Box sx={{ py: 6, minHeight: '70vh' }}>
      <SEO
        title={query ? `Search results for "${query}"` : 'Search Software Licenses'}
        description="Search our full catalog of genuine Microsoft Windows, Office, Server, and other software licenses."
      />
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 4 }}>
          <Link underline="hover" color="inherit" href="/">Home</Link>
          <Link underline="hover" color="inherit" href="/shop">Shop</Link>
          <Typography color="text.primary">Search</Typography>
        </Breadcrumbs>

        {/* Search Bar */}
        <Box sx={{ maxWidth: 620, mx: 'auto', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, textAlign: 'center', mb: 3 }}>
            {query ? `Results for "${query}"` : 'Search Products'}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="e.g. Windows 11, MS Office 2024, Windows Server..."
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            onKeyDown={handleSearch}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                fontSize: '1.05rem',
                '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Press Enter to search
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map(n => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2 }} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {/* Results count */}
            {query && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="subtitle1" color="text.secondary">
                  <strong style={{ color: '#1a1a1a' }}>{results.length}</strong> result{results.length !== 1 ? 's' : ''} found for <strong>"{query}"</strong>
                </Typography>
                {results.length > 0 && (
                  <Chip label="Clear Search" size="small" onClick={() => { setInputVal(''); setSearchParams({}); }} />
                )}
              </Box>
            )}

            <AnimatePresence mode="wait">
              {results.length > 0 ? (
                <motion.div
                  key={query}
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  <Grid container spacing={3}>
                    {results.map(product => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                        <motion.div variants={itemVariants}>
                          <ProductCard product={product} />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </motion.div>
              ) : (
                <motion.div
                  key="no-results"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 10,
                      px: 4,
                      bgcolor: '#F8FAFC',
                      borderRadius: 4,
                      border: '1.5px dashed #CBD5E1'
                    }}
                  >
                    <SearchOff sx={{ fontSize: 72, color: '#CBD5E1', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      No results found
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
                      We couldn't find any products matching "{query}". Try a different search term or browse our categories.
                    </Typography>
                    <Divider sx={{ mb: 3 }}>
                      <Typography variant="caption" color="text.secondary">Popular Searches</Typography>
                    </Divider>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {popularTags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          clickable
                          color="primary"
                          variant="outlined"
                          onClick={() => { setInputVal(tag); setSearchParams({ q: tag }); }}
                          sx={{ fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </Container>
    </Box>
  );
};

export default SearchPage;
